import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  ContentLabelField,
  ContentList,
  Divider,
  Grid,
  Select,
  Text,
  Toggle,
} from '@hopskipdrive/react-components';
import { colorTokens } from '@rideiq/shared/components/variables';

import { ListInfo } from './styles';

export interface RiderPreferencesProps {
  accountInfo?: AccountInfo;
  editMode: boolean;
}

const RiderPreferences: FC<RiderPreferencesProps> = ({ accountInfo, editMode }) => {
  const { control } = useFormContext();
  const [riderPreferences, setRiderPreferences] = useState({
    contactTripOrganizer: accountInfo?.contactTripOrganizer,
    waitTimeBeforeLeaving: accountInfo?.waitTimeBeforeLeaving,
  });

  useEffect(() => {
    setRiderPreferences({
      contactTripOrganizer: accountInfo?.contactTripOrganizer,
      waitTimeBeforeLeaving: accountInfo?.waitTimeBeforeLeaving,
    });
  }, [accountInfo]);

  return (
    <Box>
      <Text variant="h2" fontWeight={700} my={4}>
        Ride Preferences
      </Text>
      <Grid container>
        <Grid item xs={6}>
          <ContentList mb={4}>
            <Text variant="h3" fontWeight={600}>
              Who can CareDrivers contact with questions during a ride?
            </Text>
          </ContentList>
          <ContentLabelField
            id="contactRidersAndCaregivers"
            title="Riders and their Caregivers"
            description="Yes (Required)"
            editMode={false}
            editView={<Text>Yes (Required)</Text>}
          />
          <ContentLabelField
            id="contactTripOrganizer"
            title="Trip Organizer"
            description={riderPreferences.contactTripOrganizer}
            editMode={editMode}
            editView={
              <Controller
                render={({ field: { onChange } }) => (
                  <Toggle
                    value={riderPreferences.contactTripOrganizer}
                    onChange={(_, v) => {
                      if (v) {
                        onChange(v);
                        setRiderPreferences({ ...riderPreferences, contactTripOrganizer: v });
                      }
                    }}
                    exclusive
                  >
                    <Toggle.Option value="Yes">Yes</Toggle.Option>
                    <Toggle.Option value="No">No</Toggle.Option>
                  </Toggle>
                )}
                name="contactTripOrganizer"
                control={control}
              />
            }
          />
          <ContentList>
            <Text variant="h3" fontWeight={600}>
              {"How long should CareDrivers wait at a pickup if they can't find a rider?"}
            </Text>
          </ContentList>
          <ContentLabelField
            id="waitTimeBeforeLeaving"
            title="Wait time before leaving"
            description={`${riderPreferences.waitTimeBeforeLeaving} minutes`}
            editMode={editMode}
            editView={
              <Controller
                name="waitTimeBeforeLeaving"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={[
                      {
                        label: '5 minutes',
                        value: 5,
                      },
                      {
                        label: '10 minutes',
                        value: 10,
                      },
                      {
                        label: '15 minutes',
                        value: 15,
                      },
                    ]}
                    value={value}
                    setValue={onChange}
                  />
                )}
              />
            }
          />
        </Grid>
        <Grid item xs={1} style={{ maxWidth: '40px' }}>
          <Divider orientation="vertical" style={{ width: '1px' }} />
        </Grid>
        <Grid item xs={3} md={4}>
          <Box>
            <Box bgcolor={colorTokens.background.light} pb={4}>
              <ContentList>
                <Text p={8} pb={0} fontWeight={600}>
                  Riders and their caregivers are automatically notified about all details of their rides.
                  <br />
                  {"Configure what happens if the rider still can't be found."}
                </Text>
              </ContentList>
              <ContentList>
                <ListInfo>
                  <li className={'orange'}>
                    <Text className={'test-css'}>
                      {"CareDrivers still may need to call if they're having a hard time finding the rider"}
                    </Text>
                  </li>
                  <li className={'orange'}>
                    <Text>
                      If after the designated wait time the rider is still not found, HopSkipDrive will grant the
                      CareDriver permission to leave
                    </Text>
                  </li>
                </ListInfo>
              </ContentList>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RiderPreferences;
